import { useQuery } from "@tanstack/react-query";

import { api, type ArchivedCategory, type Category } from "@/api";
import {
	CLOTHES_DEPARTMENT,
	type DepartmentOption,
	departmentRootIds,
	departmentsFromTree,
	segmentRootIds,
} from "@/lib/category-tree";

/**
 * Дерево категорий из API и всё, что из него выводится: разделы шапки, корни
 * аудиторий и корни текущего раздела. Один запрос `["categories"]` на всю
 * витрину (шапка, каталог, главная, идеи).
 */
export function useCategoryTree(department?: string) {
	const query = useQuery({ queryKey: ["categories"], queryFn: () => api.categories.list() });
	const categories: Category[] = query.data ?? [];
	const departments: DepartmentOption[] = departmentsFromTree(categories);
	return {
		categories,
		departments,
		segmentRoots: segmentRootIds(categories),
		departmentRootIds: department ? departmentRootIds(categories, department) : [],
		isClothes: !department || department === CLOTHES_DEPARTMENT || !departments.some((d) => d.key === department),
		isPending: query.isPending,
		isError: query.isError,
	};
}

/**
 * Архивные узлы с преемником из шлюза (`GET /catalog/categories/archived`).
 * `archived` — `undefined`, пока ручка не ответила или упала: тогда
 * `resolveCategoryId` берёт запасную таблицу. `settled` — ответ (или отказ)
 * уже есть, по нему можно делать разовый редирект старой ссылки.
 */
export function useArchivedCategories() {
	const query = useQuery({
		queryKey: ["categories", "archived"],
		queryFn: () => api.categories.archived(),
		staleTime: 30 * 60_000,
		retry: 1,
	});
	const archived: ArchivedCategory[] | undefined = query.data;
	return { archived, settled: !query.isPending };
}
